import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { getMarketingContent, subscribeMarketingContent } from "../lib/marketingContent";
import HeroScrollReveal from "../components/HeroScrollReveal";
import { BrandCopy } from "../components/BrandWordmarkText";
import { CTA_GRADIENT, CTA_GRADIENT_HOVER, ContactSalesModal, MarketingTopBar, SiteFooter } from "../components/marketing/shared";
import { MarketingMotion } from "../components/marketing/MarketingMotion";
import { Parallax, ScrollProgressBar } from "../components/marketing/scrollMotion";
import HeroTaglineRotator, { HERO_TAGLINES } from "../components/marketing/HeroTaglineRotator";
import MarketingSeo from "../components/marketing/MarketingSeo";
import ButtonLink from "../components/marketing/ButtonLink";
import { EXPLORE_ITEMS, MARKETING_NAV_SECONDARY, demoCtaLabel } from "../components/marketing/navigation";
import { ORGANIZATION_ID, organizationSchemaFor } from "../components/marketing/organizationSchema";
import { cn } from "../lib/utils";
import {
  ShieldCheck, ArrowRight, Check, Building2, Mail, Phone, Sparkles,
} from "lucide-react";
import { Reveal, NAV_LINKS } from "./landingparts/parts1";
import { CampaignStrip, campaignFrom } from "./landingparts/campaignStrip";
import { FAQ } from "./landingparts/parts2";
import { QuickEnquiry, smoothScrollToId, scrollToDashboard, ExploreMenu, HeroCursorLensReveal } from "./landingparts/parts4";
import HeroCampusArt from "../components/marketing/HeroCampusArt";
import PremiumFaqSection from "./landingparts/PremiumFaqSection";
import WhySwitchSection, { DEFAULT_CARDS as WHY_SWITCH_DEFAULT_CARDS } from "./landingparts/WhySwitchSection";
import { init as initAnalytics, track, trackPhoneCallClick, trackWhatsAppClick, whatsappUrlWithContext } from "../lib/analytics";
import { SALES_EMAIL, SALES_PHONE } from "../lib/contactInfo";

// The governed Aira walkthrough and the wider AI suite sit below the module explorer.
// Loading them on demand keeps their interaction/data code out of the homepage entry chunk while
// the fixed-height fallbacks below preserve the narrative rhythm on slower connections.
// (The Aira guided demo + AI & Automation suite moved to /aira — WP-20; the homepage keeps
// a compact static teaser at #aira-ai, so neither heavy chunk loads here anymore.)
// These evidence-heavy homepage sections sit below the module explorer. Keep the role-switching
// screen inventory and product-proof layout outside the eager landing bundle.
const LandingMobileAppSection = React.lazy(() => import("./landingparts/LandingMobileAppSection"));
// The assistant is a persistent enhancement, not first-paint content. Keep its chat/runtime code
// in the same on-demand chunk used by the other public marketing routes instead of pulling it into
// the eager homepage bundle through a second static import.
const AiraAssistant = React.lazy(() => import("../components/AiraAssistant"));
const WhyVidyapeethSection = React.lazy(() => import("./landingparts/WhyVidyapeethSection"));
const VoiceAssistantSection = React.lazy(() => import("./landingparts/VoiceAssistantSection"));
// The complete catalogue derives from the same 115-entry registry as /modules. Keeping the rich
// explorer in a route-local chunk preserves the landing entry budget while its outer #modules
// section below provides a stable deep-link target and layout reservation.
const LandingModuleExplorer = React.lazy(() => import("./landingparts/LandingModuleExplorer"));

function DeferredSectionFallback({ className = "min-h-[28rem]" }) {
  return <div className={cn("border-b border-line bg-muted/25", className)} aria-hidden="true" />;
}

// POS-3 (2026-08-18): the capability-proof band renders the CMS social_proof.stats slot the
// founder's 2026-08-14 value-proof directive reserved ("the slot is the contract" — it was
// authored but had no public renderer). Byte-mirrors core/marketing.py
// DEFAULTS["social_proof"]["stats"] — registry facts only (M1 breadth · R1 automations ·
// A0 languages · S5 DPDP controls), never adoption metrics or slogans.
const FALLBACK_STATS = [
  { id: "s1", value: "9 workspaces", label: "50+ modules on one connected platform" },
  { id: "s2", value: "40 ready automations", label: "Reviewed starter rules — off until your school turns them on" },
  { id: "s3", value: "11 languages", label: "English + 10 Indian languages in Aira chat and voice" },
  { id: "s4", value: "DPDP-supporting controls", label: "Consent, retention and erasure workflows for your privacy programme" },
];

function LandingContent() {
  // WP-05: cross-page section links (e.g. /pricing's "Read the FAQ" -> /#faq) arrive as a
  // client navigation with a hash; react-router does not scroll to it, so we do — after a
  // short beat that lets the lazy below-fold sections mount.
  const { hash: locationHash, search: locationSearch } = useLocation();
  useEffect(() => {
    if (!locationHash) return undefined;
    let stopped = false;
    let observer;
    let frame;
    let firstAlignment = true;
    const targetId = locationHash.slice(1);
    const reduce = typeof window !== "undefined"
      && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const align = () => {
      if (stopped) return;
      const target = document.getElementById(targetId);
      if (!target) return;
      const rect = target.getBoundingClientRect();
      // Code-split sections above the demo continue resolving after the first hash scroll. Keep
      // the requested section in view as their reserved heights settle; correction passes are
      // instant so the detailed product narrative never causes a long second animation.
      if (rect.bottom <= 96 || rect.top > window.innerHeight * 0.34) {
        target.scrollIntoView({
          behavior: firstAlignment && !reduce ? "smooth" : "auto",
          block: "start",
        });
      }
      firstAlignment = false;
    };
    const scheduleAlignment = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(align);
    };
    const timer = window.setTimeout(() => {
      scheduleAlignment();
      if (typeof ResizeObserver === "function") {
        observer = new ResizeObserver(scheduleAlignment);
        observer.observe(document.getElementById("main") || document.body);
      }
    }, 140);
    const stopFollowing = () => { stopped = true; observer?.disconnect(); };
    const expiry = window.setTimeout(stopFollowing, 5000);
    // A deliberate click/tap takes ownership from the incoming deep-link alignment. Without this,
    // the ResizeObserver can pull the page back to the original hash after another CTA has already
    // moved the visitor to a new destination.
    window.addEventListener("pointerdown", stopFollowing, { passive: true, once: true });
    window.addEventListener("wheel", stopFollowing, { passive: true, once: true });
    window.addEventListener("touchstart", stopFollowing, { passive: true, once: true });
    window.addEventListener("keydown", stopFollowing, { once: true });
    return () => {
      stopped = true;
      window.clearTimeout(timer);
      window.clearTimeout(expiry);
      window.cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener("pointerdown", stopFollowing);
      window.removeEventListener("wheel", stopFollowing);
      window.removeEventListener("touchstart", stopFollowing);
      window.removeEventListener("keydown", stopFollowing);
    };
  }, [locationHash]);
  const [cms, setCms] = useState(null);
  const [salesOpen, setSalesOpen] = useState(false);

  // Marketing CMS fetch. Every visible section retains a useful authored fallback when the
  // public content endpoint is unavailable; pricing lives exclusively on /pricing.
  const loadCms = useCallback(() => {
    // Shared session-memoized fetch (lib/marketingContent) — the SiteFooter's own CMS read
    // rides the same inflight promise, so the page issues ONE /public/marketing request.
    getMarketingContent().then((data) => { if (data) setCms(data); });
  }, []);
  useEffect(() => { loadCms(); }, [loadCms]);
  // CMS-3: keep the homepage in sync with a later publish (checked right after the prerendered
  // snapshot's bootstrap loads, and again on tab refocus) instead of only ever showing the
  // deploy-time copy for the rest of the session — see lib/marketingContent.js.
  useEffect(() => subscribeMarketingContent((data) => setCms(data)), []);

  // Marketing analytics: idempotent env-gated init (captures UTM attribution).
  useEffect(() => {
    initAnalytics();
  }, []);

  // "Book Demo" CTAs scroll to the inline demo form ("See Vidyapeeth360 in Action") instead of opening
  // the sales modal — same-page, smooth (honors reduced-motion), and the section's scroll-mt clears the sticky nav.
  const scrollToDemoSection = (placement, { trackRequest = true } = {}) => {
    // conversion signal: every "Book Demo" CTA funnels through here (placement = which button)
    if (trackRequest) track("demo_request", { placement: typeof placement === "string" ? placement : "landing" });
    const el = document.getElementById("see-vidyapeeth360-in-action");
    if (!el) { setSalesOpen(true); return; } // register/demo section hidden by the admin → sales modal, never a dead button
    // the section defaults to self-serve registration — a "Book Demo" CTA must land on the demo form
    window.dispatchEvent(new CustomEvent("vp-open-demo-form"));
    const reduce = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
  };

  const faq = (cms?.faq && cms.faq.length) ? cms.faq : FAQ;
  // Platform-admin section visibility. Defaults remain visible until the CMS loads; a stored
  // `false` then hides the corresponding narrative section end-to-end.
  const showHero = cms?.sections?.hero !== false;
  const showSocial = cms?.sections?.social_proof !== false;
  const showFaq = cms?.sections?.faq !== false;
  const showWhySwitch = cms?.sections?.why_switch !== false;
  const showModules = cms?.sections?.modules !== false;
  const showAiraDemo = cms?.sections?.aira_demo !== false;
  const showMobileApp = cms?.sections?.mobile_app !== false;
  const showWhyVp360 = cms?.sections?.why_vp360 !== false;
  const showVoiceAi = cms?.sections?.voice_ai !== false;
  const voiceAi = cms?.voice_ai || {};
  const showRegister = cms?.sections?.register !== false;
  const isBookDemoIntent = useMemo(
    () => new URLSearchParams(locationSearch).get("intent") === "book-demo",
    [locationSearch],
  );
  useEffect(() => {
    if (
      !cms
      || showRegister
      || !isBookDemoIntent
      || locationHash !== "#see-vidyapeeth360-in-action"
    ) return undefined;
    // A shareable demo URL must never become a dead anchor when Marketing CMS hides the inline
    // enquiry section. Wait through the hash-scroll attempt, then open the same governed sales
    // fallback used by in-page Book Demo controls.
    const timer = window.setTimeout(() => {
      if (!document.getElementById("see-vidyapeeth360-in-action")) setSalesOpen(true);
    }, 180);
    return () => window.clearTimeout(timer);
  }, [cms, isBookDemoIntent, locationHash, showRegister]);
  const hero = { ...(cms?.hero || {}) };
  const heroHeadline = hero.headline || "The connected school ERP.";
  const heroHighlight = hero.highlight || HERO_TAGLINES[0];
  // `highlight` remains the stable accessible/opening phrase for older CMS revisions. A newly
  // supplied taglines list extends it; before the CMS response arrives (or for an older revision),
  // the grounded code defaults provide the cycle.
  const heroTaglines = useMemo(() => {
    const configured = Array.isArray(hero.taglines) ? hero.taglines : HERO_TAGLINES;
    const seen = new Set();
    return [heroHighlight, ...configured].filter((value) => {
      if (typeof value !== "string" || !value.trim()) return false;
      const key = value.trim().toLocaleLowerCase("en-IN");
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    }).map((value) => value.trim());
  }, [hero.taglines, heroHighlight]);
  // WP-09: the h1 renders the highlight STATICALLY; the aria-hidden typewriter below it cycles
  // only the REMAINING taglines (never duplicating the static line).
  const rotatorTaglines = useMemo(
    () => heroTaglines.filter((value) => value.trim().toLocaleLowerCase("en-IN") !== heroHighlight.trim().toLocaleLowerCase("en-IN")),
    [heroTaglines, heroHighlight],
  );
  const social = cms?.social_proof || null;
  // Section copy from the Marketing CMS — every inline fallback below MUST mirror the matching
  // core/marketing.py DEFAULTS entry byte-for-byte (same discipline as `hero`), so a failed CMS
  // fetch renders today's site unchanged.
  const modulesCopy = cms?.modules || {};
  const mobileApp = cms?.mobile_app || {};
  const whyVp = cms?.why_vp360 || {};
  // POS-11: the three differentiation headlines the Why-switch band answers in full, condensed
  // for the strip that sits ABOVE the module catalogue. Read from the SAME CMS list the band
  // renders (its exported fallback covers a failed CMS fetch), so the two can never fork.
  const whySwitchChips = useMemo(() => {
    const cards = Array.isArray(cms?.why_switch?.cards) && cms.why_switch.cards.length
      ? cms.why_switch.cards
      : WHY_SWITCH_DEFAULT_CARDS;
    return cards
      .map((card) => (card?.title || "").trim())
      .filter(Boolean)
      .slice(0, 3);
  }, [cms]);
  // Route links (/modules, /pricing) always render — their destinations exist regardless of
  // homepage section visibility. Only in-page anchors are gated by their CMS section toggle.
  const navLinks = NAV_LINKS.filter((l) =>
    (l.href !== "#faq" || showFaq) && (l.href !== "#why" || showWhyVp360));

  return (
    <div className="landing-page relative isolate min-h-screen bg-surface transition-colors duration-300">
      <MarketingSeo
        title="School Management Software India — ERP + AI | Vidyapeeth360"
        description="A connected K–12 school ERP for admissions, attendance, fees in ₹, exams, records, transport and parent communication, with governed AI where enabled."
        path="/"
        schema={{
          // WP-01/WP-21: moved out of public/index.html so this ships on the homepage snapshot
          // only; the standalone Organization node (real legal name + CIN + registered office)
          // is the shared publisher reference.
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "SoftwareApplication",
              name: "Vidyapeeth360",
              applicationCategory: "BusinessApplication",
              operatingSystem: "Web",
              description: "A connected school-management platform built around Indian K–12 operations, with governed AI assistance where enabled.",
              url: "https://vidyapeeth360.com/",
              publisher: { "@id": ORGANIZATION_ID },
            },
            {
              // The site-level node search engines use for the site name; publisher points at
              // the shared Organization node (real legal identity) rather than repeating it.
              "@type": "WebSite",
              "@id": "https://vidyapeeth360.com/#website",
              name: "Vidyapeeth360",
              url: "https://vidyapeeth360.com/",
              publisher: { "@id": ORGANIZATION_ID },
            },
            // sameAs rides the Organization node only when the CMS carries published profiles
            // (TRUST-15); MarketingSeo re-applies the block when the schema JSON changes.
            organizationSchemaFor(cms?.social_links?.links),
          ],
        }}
      />
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[60] focus:bg-primary focus:text-white focus:px-4 focus:py-2 focus:rounded-lg">Skip to content</a>

      {/* Dark premium canopy — sits behind the header + hero so the navy reaches the very top of the
          page (matches the dark hero). It paints above the page's own bg-surface (-z-10) but below all
          opaque content sections, which cover it from the hero downward. Height bounds the header only;
          the hero carries its own matching dark background below. */}
      <div className="hero-canopy pointer-events-none absolute top-0 inset-x-0 -z-10 h-[150px] md:h-[188px] overflow-hidden" aria-hidden="true" />

      {/* Thin contact strip — WP-19: ONE sales number + WhatsApp (not three equal numbers);
          the full labelled channel list lives on /contact. */}
      <div className="hidden md:block" role="group" aria-label="Contact information">
        <div className="max-w-[90rem] mx-auto px-7 h-8 flex items-center justify-between text-[0.72rem] hero-scene-ink-dim">
          <div className="flex items-center gap-5">
            <a href={`tel:${SALES_PHONE.tel}`} onClick={() => trackPhoneCallClick("landing_strip", "sales")} className="inline-flex items-center gap-1.5 hero-scene-link transition-colors"><Phone className="w-3 h-3" aria-hidden="true" /> {SALES_PHONE.display}</a>
            {/* CRO-11: the WhatsApp-first ICP lands in chat with the evaluation context prefilled
                (visible + editable before sending — never PII). */}
            <a href={whatsappUrlWithContext("the homepage")} target="_blank" rel="noopener noreferrer" onClick={() => trackWhatsAppClick("landing_strip")} className="inline-flex items-center gap-1.5 hero-scene-link transition-colors">WhatsApp us</a>
            <a href={`mailto:${SALES_EMAIL}`} className="inline-flex items-center gap-1.5 hero-scene-link transition-colors"><Mail className="w-3 h-3" aria-hidden="true" /> {SALES_EMAIL}</a>
          </div>
          <div className="flex items-center gap-5">
            <Link to="/contact" className="hero-scene-link transition-colors" data-testid="utility-contact">Contact</Link>
            <Link to="/help" className="hero-scene-link transition-colors">Help Center</Link>
          </div>
        </div>
      </div>

      <ScrollProgressBar />
      <MarketingTopBar
        home
        showLanguage
        navItems={navLinks}
        // UX-08: the homepage phone panel gets the shared secondary destinations (Contact, Help,
        // Aira, Security, product tour) — passing [] left a phone visitor with no Contact link
        // outside the footer (the utility strip above is hidden below md).
        secondaryItems={MARKETING_NAV_SECONDARY}
        desktopExtra={<ExploreMenu light={false} />}
        mobileExtraItems={EXPLORE_ITEMS.filter((item) => !item.to
          || (!navLinks.some((link) => link.to === item.to)
            && !MARKETING_NAV_SECONDARY.some((link) => link.to === item.to)))}
        onBookDemo={(detail) => scrollToDemoSection(detail?.placement || "nav", { trackRequest: !detail?.analyticsTracked })}
        onNavItem={(item, event) => {
          if (!item.dashboard) return;
          event.preventDefault();
          scrollToDashboard();
        }}
        onMobileExtra={(item, event) => {
          if (!item.scrollTo) return;
          event.preventDefault();
          const target = item.scrollTo === "aira-ai" ? (showAiraDemo ? "aira-ai" : null) : item.scrollTo;
          if (target) window.setTimeout(() => smoothScrollToId(target), 60);
        }}
      />

      <main id="main">
        {/* Hero — centered dark premium SaaS: headline + CTAs, then a large education-ERP dashboard
            preview. The page's dark canopy (above) carries the navy up behind the navbar; this section
            supplies its own matching dark background + soft blue glow, warm-orange accent + a blue
            "lightning" beam spilling from behind the dashboard's top edge. */}
        {/* Scenic scroll stage (2026-08-22): the campus painting rides as a sticky full-viewport
            backdrop under the hero + capability proof + testimony + why-switch strip — four
            sections — with those sections in campus-glass so the artwork reads through. Light
            scene only; night keeps its committed midnight look and solid fills. */}
        <div className="hero-campus-stage-wrap">
        <HeroCampusArt artwork="landing" stage veilClassName="hero-campus-veil-landing" />
        {showHero && (
        <section className="hero-dark relative isolate hero-scene-ink" aria-label="Vidyapeeth360 for Indian K–12 schools">
          {/* cursor-trailing glass lens that reveals the faint repeating "Vidyapeeth360" brand
              watermark — background texture, behind the copy + dashboard (desktop, motion-on only) */}
          <HeroCursorLensReveal />
          <HeroScrollReveal>
            {/* WP-08: the above-the-fold hero paints STATICALLY at opacity 1 — no mount-play
                reveal cascade. The old Stagger play="mount" left the headline/CTAs invisible for
                1–3s on load and replayed from opacity 0 over hydrated prerender content. Reveal
                animation stays a below-the-fold whileInView affordance only. */}
            <div>
              <p className="snake-chip-wrap inline-flex mb-6">
                <span className="snake-chip-glow" aria-hidden="true" />
                <span className="snake-chip inline-flex">
                  <span className="snake-chip-inner inline-flex items-center justify-center gap-2 px-3.5 py-1.5 text-[13px] font-semibold tracking-[-0.01em]">
                    <span className="w-1.5 h-1.5 rounded-full bg-orange" aria-hidden="true" /> {hero.badge || "Built around Indian K–12 operations"}
                  </span>
                </span>
              </p>
              {/* WP-09: the h1 is STATIC and complete (headline + highlight) — byte-identical on
                  every read. The typewriter lives OUTSIDE the h1, aria-hidden, with its own
                  reserved line box (HeroTaglineRotator's grid-cell reserve — zero CLS).
                  Fallbacks MUST mirror core/marketing.py DEFAULTS["hero"]. */}
              <h1 style={{ letterSpacing: "-0.04em" }} className="font-heading font-extrabold text-[clamp(38px,6vw,72px)] leading-[1.04] hero-scene-ink mx-auto">
                {heroHeadline === "The Connected School ERP." || heroHeadline === "The connected school ERP."
                  ? <>The connected <span className="hero-grad-text">school ERP.</span></>
                  : <span className="hero-grad-text">{heroHeadline}</span>}
                <span className="block">{heroHighlight}</span>
              </h1>
              {rotatorTaglines.length > 0 && (
                <div aria-hidden="true" className="font-heading font-extrabold text-[clamp(22px,3.2vw,36px)] leading-[1.1] hero-scene-ink-soft mt-2" style={{ letterSpacing: "-0.03em" }}>
                  <HeroTaglineRotator
                    fallbackText={rotatorTaglines[0]}
                    loop
                    taglines={rotatorTaglines}
                  />
                </div>
              )}
              <p className="hero-scene-ink-body text-lg mt-6 max-w-2xl mx-auto leading-relaxed [text-wrap:pretty]">
                {hero.subhead || "Run admissions, attendance, exams, records, rupee fee collection and parent communication — 50+ modules on one platform. Aira and automation come with the plans that include them. You see exactly what your plan covers before you pay."}
              </p>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 mt-9">
                {/* Primary conversion: bring the evaluator to the live-demo form (or open the
                    contact modal when that form is hidden by the marketing CMS). The historical
                    test id stays stable for downstream automation.
                    CRO-9 — the hero-vs-topbar "competing primary" question, decided: they are two
                    different jobs, not two primaries. The HERO stays DEMO-first (prove-then-ask:
                    the page has shown nothing yet, so the ask is a guided walkthrough), while the
                    persistent TOPBAR stays TRIAL-first (a visitor who is already convinced can
                    self-serve from any scroll position). Both labels come from the governed
                    vocabulary, so the two never drift apart in wording. Which of the two converts
                    better is an A/B question that needs live GA4 volume — the click events
                    (`demo_request` placement=hero_primary vs `trial_cta_click` placement=topbar)
                    are already instrumented to answer it. */}
                <button type="button" onClick={() => scrollToDemoSection("hero_primary")} data-testid="hero-register"
                  className={`group inline-flex items-center justify-center gap-2 whitespace-nowrap h-[52px] px-7 rounded-full text-white text-[15px] font-semibold ${CTA_GRADIENT} shadow-[0_10px_32px_-6px_rgba(29,78,216,0.65)] ${CTA_GRADIENT_HOVER} hover:-translate-y-0.5 hover:shadow-[0_16px_40px_-8px_rgba(29,78,216,0.7)] active:scale-[0.98] transition-[transform,background-image,box-shadow] duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-focusring/70 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent`}>
                  {/* POS-6: the governed demo vocabulary ("live" context) — byte-mirrors
                      core/marketing.py DEFAULTS["hero"]["primary_cta"]. */}
                  {hero.primary_cta || demoCtaLabel("live")} <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" aria-hidden="true" />
                </button>
                {/* Secondary path is a ROUTE — so it renders as a real link (WP-05):
                    middle-clickable, shareable, crawlable. */}
                <Link to="/modules" data-testid="hero-aira"
                  className="group inline-flex items-center justify-center gap-2 whitespace-nowrap h-[52px] px-7 rounded-full text-[15px] font-semibold border hero-cta-glass backdrop-blur-md hover:-translate-y-0.5 hover:shadow-[0_12px_32px_-8px_rgba(37,99,235,0.35)] active:scale-[0.98] transition-[transform,background-color,border-color,box-shadow] duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-focusring/70 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent">
                  <Building2 className="w-4 h-4" aria-hidden="true" /> {hero.secondary_cta || "Explore 50+ modules"}
                </Link>
              </div>
              {/* PGC-7: single-fact trust chips — the merged "No credit card · migration preview"
                  chip read as one blurred claim; each fact now stands alone. */}
              <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mt-7 text-sm hero-scene-ink-faint">
                {[`Free ${cms?.pricing_integrity?.trial?.campaign_months ? `${cms.pricing_integrity.trial.campaign_months}-month` : "90-day"} trial`, "No credit card", "Student CSV/XLSX migration preview"].map((t) => (
                  <span key={t} className="inline-flex items-center gap-1.5"><Check className="w-4 h-4 text-sky shrink-0" aria-hidden="true" /> {t}</span>
                ))}
              </div>
            </div>
          </HeroScrollReveal>
        </section>
        )}

        {/* Governed pricing CAMPAIGN (launch/festival offer) — the ONE promotional strip on the
            landing surface, directly under the hero. Server-resolved + client date-guard; renders
            null outside its window, when disabled, or when its linked coupon dies (fail-closed).
            The CTA carries the visitor to /pricing, where the discounted cards live. */}
        {campaignFrom(cms?.pricing_integrity) && (
          <div className="mx-auto max-w-6xl px-5 pt-6">
            <CampaignStrip
              campaign={campaignFrom(cms?.pricing_integrity)}
              cta={{ to: "/pricing", label: "See the offer pricing" }}
              className="mb-0"
            />
          </div>
        )}

        {/* POS-3/POS-11: registry-fact capability proof directly under the hero, BEFORE the
            module catalogue — differentiation precedes inventory. Values are product facts
            (never adoption metrics); the CMS list swaps to verified traction numbers later. */}
        {showSocial && (
          <section aria-label="What the product includes, at a glance" className="border-b border-line campus-glass-surface">
            <div className="mx-auto max-w-6xl px-5 py-8 md:py-10">
              <ul data-testid="social-proof-stats" className="grid grid-cols-2 gap-x-6 gap-y-7 text-center md:grid-cols-4">
                {((social?.stats && social.stats.length) ? social.stats : FALLBACK_STATS).slice(0, 4).map((s) => (
                  <li key={s.id || s.value}>
                    <p className="font-heading font-bold text-[19px] sm:text-[21px] leading-tight tracking-tight text-ink"><BrandCopy>{s.value}</BrandCopy></p>
                    <p className="mx-auto mt-1.5 max-w-[16rem] text-[13px] leading-snug text-ink-2"><BrandCopy>{s.label}</BrandCopy></p>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        {/* Curated school testimony can earn a place here. Generic capability statements and
            unverified adoption figures do not render as social proof. An empty CMS testimonial
            list therefore occupies no space and causes no loading shift. */}
        {showSocial && (social?.testimonials || []).length > 0 && (
          <section aria-labelledby="social-proof-heading" className="border-b border-line campus-glass-surface">
            <div className="mx-auto max-w-6xl px-5 py-14 md:py-16">
              <Reveal className="mb-8 max-w-3xl">
                <h2 id="social-proof-heading" className="font-heading font-bold text-[26px] sm:text-[32px] text-ink tracking-tight"><BrandCopy>{social.heading}</BrandCopy></h2>
                {social.subhead && <p className="text-ink-2 mt-2.5 max-w-2xl mx-auto"><BrandCopy>{social.subhead}</BrandCopy></p>}
              </Reveal>
              <div className="grid gap-px overflow-hidden rounded-2xl border border-line bg-line md:grid-cols-3" data-testid="social-proof-testimonials">
                {social.testimonials.map((t) => (
                    <figure key={t.id || t.quote} className="bg-surface p-6">
                      <blockquote className="text-[15px] text-ink-2 leading-relaxed">“<BrandCopy>{t.quote}</BrandCopy>”</blockquote>
                      <figcaption className="mt-4 text-sm">
                        <span className="font-semibold text-ink"><BrandCopy>{t.name}</BrandCopy></span>
                        {(t.role || t.school) && <span className="text-ink-subtle"> — <BrandCopy>{[t.role, t.school].filter(Boolean).join(", ")}</BrandCopy></span>}
                      </figcaption>
                    </figure>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* POS-11: differentiation precedes the catalogue. A CONDENSED strip of the Why-switch
            band (the same three answers, same CMS source) sits directly above #modules and links
            down to the full band — so an evaluator meets "why move at all" before the 50+ module
            inventory, instead of only after it. Renders only when both sections are enabled and
            the CMS actually holds the copy. */}
        {showModules && showWhySwitch && whySwitchChips.length > 0 && (
          <section aria-label="Why schools switch: summary" className="border-b border-line campus-glass-surface">
            <div className="mx-auto flex max-w-6xl flex-col gap-3 px-5 py-6 md:flex-row md:items-center md:gap-7 md:py-7">
              <p className="shrink-0 text-[11px] font-bold uppercase tracking-[0.16em] text-ink-subtle">Why schools switch</p>
              <ul data-testid="why-switch-preview" className="flex flex-wrap gap-2">
                {whySwitchChips.map((chip) => (
                  <li key={chip}>
                    <a
                      href="#why-switch"
                      onClick={(event) => {
                        if (!document.getElementById("why-switch")) return;
                        event.preventDefault();
                        smoothScrollToId("why-switch");
                      }}
                      className="inline-flex min-h-9 items-center gap-2 rounded-xl border border-line bg-surface px-3 text-[12.5px] font-semibold text-ink-2 transition-[background-color,border-color,color] duration-fast ease-premium hover:border-accent/30 hover:bg-muted hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-focusring/55"
                    >
                      <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-accent-active" aria-hidden="true" />
                      <BrandCopy>{chip}</BrandCopy>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        {showModules && (
          <section
            id="modules"
            tabIndex={-1}
            className="scroll-mt-24 overflow-hidden border-y border-line campus-glass-muted60 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/45"
            aria-labelledby="modules-heading"
          >
            <React.Suspense fallback={<DeferredSectionFallback className="min-h-[46rem]" />}>
              <LandingModuleExplorer content={modulesCopy} />
            </React.Suspense>
          </section>
        )}

        {/* 12. Aira AI teaser — the extended Aira story (guided chat demo + AI & Automation
            suite) lives on /aira (WP-20 homepage-length decision); this compact panel keeps the
            #aira-ai anchor, the vetted headline copy and the live-assistant opener, and links
            through. Toggleable via sections.aira_demo; the anchor unmounts with the section. */}
        {showAiraDemo && (
          <>
            {/* hero "Aira AI" CTA scroll target */}
            <div id="aira-ai" aria-hidden="true" className="scroll-mt-24" />
            <section id="ai" aria-labelledby="aira-teaser-heading" data-testid="aira-teaser"
              className="relative scroll-mt-24 overflow-hidden border-y border-line campus-glass-primary py-14 text-ink sm:py-16">
              {/* Scroll-linked depth (2026-08-20): the decorative washes drift a beat slower than
                  the copy. One Parallax wrapper for both theme layers; edge-transparent gradients
                  mean the drift never exposes a seam, and reduce/pause is byte-identical. */}
              <Parallax className="pointer-events-none absolute inset-0" distance={22}>
                <div className="absolute inset-0 dark:hidden" aria-hidden="true"
                  style={{ backgroundImage: "radial-gradient(circle at 78% 15%, rgba(124,58,237,0.15), transparent 31%), radial-gradient(circle at 12% 43%, rgba(37,99,235,0.10), transparent 33%)" }} />
                <div className="absolute inset-0 hidden dark:block" aria-hidden="true"
                  style={{ backgroundImage: "radial-gradient(circle at 77% 14%, rgba(139,92,246,0.22), transparent 34%), linear-gradient(180deg, #070b18 0%, #0b1024 100%)" }} />
              </Parallax>
              <div className="relative mx-auto w-full max-w-4xl px-5 text-center sm:px-6">
                <span className="inline-flex min-h-8 items-center gap-2 rounded-full border border-primary/20 bg-surface/80 px-3 text-xs font-semibold tracking-wide text-accent-active shadow-card">
                  <Sparkles className="h-3.5 w-3.5" aria-hidden="true" /> Aira · governed school intelligence
                </span>
                <h2 id="aira-teaser-heading" className="mt-5 text-balance font-heading text-3xl font-bold tracking-[-0.03em] text-ink sm:text-4xl">
                  Ask Aira AI.{" "}
                  <span className="text-accent-active">Get grounded answers.</span>
                </h2>
                {/* UX-06: plain language (grade ≈ 6, was 18) — same boundary claims: tenant +
                    permission scoping, and human confirmation before any action. */}
                <p className="mx-auto mt-4 max-w-[38rem] text-pretty text-base leading-7 text-ink-2">
                  Aira answers from your school&rsquo;s own data, limited to what each role may see.
                  It can suggest actions — a person approves them first.
                </p>
                <ul className="mx-auto mt-6 flex max-w-2xl flex-wrap items-center justify-center gap-2" aria-label="Aira safeguards and language coverage">
                  {["Role-scoped context", "Human approval gates", "Action evidence", "11 language options"].map((t) => (
                    <li key={t} className="inline-flex min-h-9 items-center gap-2 rounded-xl border border-line bg-surface px-3 text-xs font-semibold text-ink-2 shadow-card">
                      <ShieldCheck className="h-3.5 w-3.5 text-accent-active" aria-hidden="true" /> {t}
                    </li>
                  ))}
                </ul>
                <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
                  <ButtonLink to="/aira" data-testid="aira-teaser-link"
                    className="w-full justify-center !rounded-full !bg-[linear-gradient(110deg,#2457ea_0%,#4f46e5_52%,#7c3aed_100%)] !text-white shadow-[0_14px_30px_-14px_rgba(92,55,226,0.78)] hover:brightness-105 sm:w-auto">
                    See how Aira answers a question <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </ButtonLink>
                  <button type="button" data-testid="aira-try"
                    onClick={() => {
                      track("aira_demo_interact", { surface: "landing_teaser_cta" });
                      window.dispatchEvent(new CustomEvent("vp-aira-open"));
                    }}
                    className="inline-flex min-h-11 w-full items-center justify-center rounded-full border border-primary/25 bg-surface px-5 text-sm font-semibold text-accent-active transition-[background-color,border-color,color,transform] duration-fast ease-premium hover:border-primary/40 hover:bg-surface/70 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focusring focus-visible:ring-offset-2 sm:w-auto">
                    Ask our website assistant a question
                  </button>
                </div>
              </div>
            </section>
          </>
        )}

        {/* Voice intelligence: extends the Aira story the teaser opens — multilingual voice,
            speech→clean-prompt, AI meeting notes. Below the visual-gate band by design. */}
        {showVoiceAi && (
          <React.Suspense fallback={<DeferredSectionFallback className="min-h-[44rem]" />}>
            <VoiceAssistantSection content={voiceAi} />
          </React.Suspense>
        )}

        {/* Platform thesis: the six buyer-facing journeys lead into the operating model that
            connects records, rules, owners and outcomes. Procurement objections come afterwards.
            2026-08-22: rides the campus stage — the painting follows through these sections too
            (their roots are campus-glass; see WhyVidyapeethSection/WhySwitchSection). */}
        {showWhyVp360 && (
          <React.Suspense fallback={<DeferredSectionFallback className="min-h-[48rem]" />}>
            <WhyVidyapeethSection content={whyVp} onBookDemo={() => scrollToDemoSection("why_live_demo")} />
          </React.Suspense>
        )}

        {/* Migration, payment-evidence and trial objections land after the evaluator has seen the
            connected suite, governed AI and the connected operating model. */}
        {showWhySwitch && <WhySwitchSection content={cms?.why_switch} />}
        </div>

        {/* Homepage preview: approved real mobile captures only. The detailed role/module explorer
            remains on /mobile-app; this section preserves the CMS's visibility and authored copy. */}
        {showMobileApp && (
          <React.Suspense fallback={<DeferredSectionFallback className="min-h-[52rem]" />}>
            <LandingMobileAppSection content={mobileApp} />
          </React.Suspense>
        )}

        {/* FAQ — CMS content stays authoritative; the premium accordion only changes presentation. */}
        {showFaq && (
          <PremiumFaqSection
            faq={faq}
            onBookReview={() => scrollToDemoSection("faq_workflow_review")}
          />
        )}

        {/* Register / demo form — the page's ASK, mounted at the end of the narrative after the
            product has proved itself (prove-then-ask, MARKETING_10X_PLAN Phase-2 §2). Hero/nav CTAs
            deep-link here via #register-your-school / #see-vidyapeeth360-in-action. Demo CTAs are
            guarded when hidden and fall back to the contact-sales modal.
            Toggleable: sections.register (visibility flag only). */}
        {showRegister && <QuickEnquiry />}

        <ContactSalesModal open={salesOpen} onClose={() => setSalesOpen(false)} />
      </main>

      {/* Footer — the shared canonical marketing footer (consistent across every marketing page) */}
      <SiteFooter />

      {/* Vidyapeeth360 Aira Assistant (bottom-right) — the single landing chat widget: LLM answers
          grounded on the full public Help/KB corpus with article deep-links, a 50-free-question
          session quota, and lead capture that files a platform sales lead + support ticket. */}
      <React.Suspense fallback={null}>
        <AiraAssistant onDemo={() => scrollToDemoSection("aira_widget")} />
      </React.Suspense>
    </div>
  );
}

// Mount the page's motion-aware hooks below the shared provider so the footer Pause control stops
// Framer loops, custom requestAnimationFrame effects and smooth-scroll decisions together.
export default function Landing() {
  return <MarketingMotion><LandingContent /></MarketingMotion>;
}
